import { ChangeDetectionStrategy, Component, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { FiRemoteStateFullDataSource } from '@fi-shared/fi-items-list/fi-remote-state-full-data-source';
import { ITaskV2 } from '../../task-v2/fi-task-v2';
import { MatSortDirection, ObjectTypes, SideListOptionType } from '@fi-shared/types';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { ILoadDataArgs } from '@fi-shared/fi-items-list/fi-data-source-base.directive';
import {
    IItemsListAction2,
    IItemsListDataFetchResult,
    IItemsListTopAction,
    ISideListOptionsItem,
} from '@fi-shared/fi-items-list/fi-items-list';
import { FiTaskV2Service } from '../../task-v2/fi-task-v2.service';
import { FiTaskV2HelperService } from '../../task-v2/fi-task-v2-helper.service';
import { FiItemsListOptionType } from '@fi-shared/fi-items-list/fi-items-list-option-type';
import { BehaviorSubject, Observable, takeUntil } from 'rxjs';
import { Animations } from '@fi-shared/utils/animations';
import { ITaskParameter } from '@fi-shared/fi-task-shared/fi-task-shared';
import { FiTasksViewMode } from '@fi-shared/fi-task-shared/fi-task-enums';
import { FiTaskV2SyncService } from '../fi-task-v2-sync.service';
import { FiTaskSyncService } from '@fi-shared/fi-task-shared/fi-task-sync-service';
import { DeepReadonly } from '@fi-app/shared/types/deep-readonly';
import { FiTaskV2ListHelperService } from '../fi-task-v2-list/fi-task-v2-list-helper.service';
import { FiTaskCategoryType } from '../fi-task-v2-enums';
import { TaskActionsBuilderService } from '../fi-task-v2-report/fi-task-v2-report-widget-details/widget-details-content/widget-details-table/task-actions.builder.service';
import { FiTaskStatusesConfig } from '@fi-app/task-v2/fi-task-v2-consts';

@Component({
    selector: 'fi-task-v2-archived-list',
    templateUrl: './fi-task-v2-archived-list.component.html',
    styleUrl: './fi-task-v2-archived-list.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-task-v2-archived-list' },
    animations: [Animations.fadeHeight()],
    standalone: false,
})
export class FiTaskV2ArchivedListComponent extends FiRemoteStateFullDataSource<ITaskV2> implements OnInit {
    defaultSortColumnName = 'ArchivedOn';
    defaultSortDirection = true;
    objectType = ObjectTypes.Task2;
    itemsListType = FiItemsListType.ArchivedTaskListData;
    filterType = SideListOptionType.SharedWithMe;
    protected readonly MatSortDirection = MatSortDirection;

    protected readonly FiTaskStatusesConfig = FiTaskStatusesConfig;

    private readonly fiTaskV2ListHelperService = inject(FiTaskV2ListHelperService);
    private readonly fiTaskV2Service = inject(FiTaskV2Service);
    private readonly fiTaskV2HelperService = inject(FiTaskV2HelperService);
    private readonly fiTaskV2SyncService = inject(FiTaskV2SyncService);
    private readonly fiTaskSyncService = inject(FiTaskSyncService);
    private readonly fiTaskActionsBuilderService = inject(TaskActionsBuilderService);

    protected readonly sideListOptions: DeepReadonly<ISideListOptionsItem[]> =
        this.fiTaskV2ListHelperService.archivedSideListOptions;
    protected readonly taskActionItems: Readonly<IItemsListTopAction[]> = [
        {
            title: this.appTextService.get('task.restore_tasks'),
            iconClass: 'fa-regular fa-arrow-rotate-left',
            onClick: () => {
                this.restoreArchivedTasks(this.selectedItems());
            },
        },
        {
            title: this.appTextService.get('common.delete_permanently'),
            iconClass: 'fa-regular fa-trash',
            onClick: () => {
                this.showConfirmDeleteDialog(this.selectedItems());
            },
        },
    ];
    protected readonly actions: Readonly<IItemsListAction2[]> = [
        {
            title: this.appTextService.get('common.restore'),
            condition: (item) => {
                return this.fiTaskActionsBuilderService.canRestoreDeleteTask(item);
            },
            onClick: (item) => {
                this.restoreArchivedTasks([item]);
            },
            iconClass: 'fa-regular fa-arrow-rotate-left',
            optionType: FiItemsListOptionType.Icon,
        },
        {
            title: this.appTextService.get('common.delete_permanently'),
            condition: (item) => {
                return this.fiTaskActionsBuilderService.canRestoreDeleteTask(item);
            },
            onClick: (item) => {
                this.showConfirmDeleteDialog([item]);
            },
            iconClass: 'fa-regular fa-trash',
            optionType: FiItemsListOptionType.Icon,
        },
    ];

    private _isDescriptionVisible$ = new BehaviorSubject<boolean>(false);
    get isDescriptionVisible$(): Observable<boolean> {
        return this._isDescriptionVisible$.asObservable();
    }

    public ngOnInit(): void {
        this.fiTaskV2SyncService.loadTasksCountForTabs(FiTasksViewMode.archived);
        this.subscribeToSyncTasks();
    }

    private subscribeToSyncTasks(): void {
        this.fiTaskSyncService.updatedTasksSyncResult$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            this.fiTaskV2SyncService
                .loadTasksCountForTabs(FiTasksViewMode.archived)
                .then(() => this.cdr.markForCheck());
        });
    }

    public loadListDataFn(loadArgs: ILoadDataArgs): Promise<IItemsListDataFetchResult<ITaskV2>> {
        return new Promise<IItemsListDataFetchResult<ITaskV2>>((resolve, reject) => {
            const parameter = this.getParameter(loadArgs);
            this.fiTaskV2Service.getTasksListData(parameter).then((data) => {
                resolve(data);
            }, reject);
        });
    }

    protected getCategoryText(category: FiTaskCategoryType): string {
        return this.fiTaskV2HelperService.getTaskCategoryText(category);
    }

    protected isCheckboxItemUnavailable(item: ITaskV2): boolean {
        return !this.fiTaskActionsBuilderService.canRestoreDeleteTask(item);
    }

    protected isCategoryColumnShown(): boolean {
        return this.filterType === SideListOptionType.SharedWithMe;
    }

    protected toggleDescriptionVisibility(): void {
        this._isDescriptionVisible$.next(!this._isDescriptionVisible$.value);
    }

    private restoreArchivedTasks(tasks: ITaskV2[]): void {
        this.fiTaskV2Service.saveTaskArchived(tasks, false).then(() => {
            this.removeData(tasks);
            this.fiTaskV2HelperService.showTaskRestoredSnackbar(tasks[0].Name, {
                tasksRestoredCount: tasks.length,
            });
        });
    }

    private showConfirmDeleteDialog(tasks: ITaskV2[]): void {
        this.fiTaskV2HelperService.showDeleteTaskConfirmDialog(tasks.length).onYesButtonClick(() => {
            this.deleteArchivedTasks(tasks);
        });
    }

    private deleteArchivedTasks(tasks: ITaskV2[]): void {
        this.fiTaskV2Service
            .deleteTasks(
                tasks.map((item) => item.Id),
                true,
            )
            .then(() => {
                this.removeData(tasks);
                this.fiTaskV2HelperService.showTaskDeletedSnackbar(tasks[0].Name, tasks.length);
            });
    }

    private getParameter(loadArgs: ILoadDataArgs): ITaskParameter {
        const parameter = super.getItemsListParameter(loadArgs) as ITaskParameter;
        return this.fiTaskV2HelperService.getUpdatedTaskParameter(parameter, loadArgs, {
            filterType: this.filterType,
            isArchived: true,
        });
    }
}
